import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { APP_VERSION, APP_BUILD } from "@/version";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Users2, CalendarClock, ListTodo, LogOut, FileText, GitBranch,
  LayoutDashboard, FlaskConical, Package, Boxes, Layers, BookOpen,
} from "lucide-react";
import DashboardTab from "./sales/DashboardTab";
import ActiesTab from "./sales/ActiesTab";
import AfsprakenTab from "./sales/AfsprakenTab";
import OffertesTab from "./OffertesTab";
import OrdersTab from "./sales/OrdersTab";
import PijplijnTab from "./sales/PijplijnTab";
import AanvragenTab from "./sales/AanvragenTab";
import KlantenTab from "./sales/KlantenTab";
import ArtikelenTab from "./sales/ArtikelenTab";
import FoliesTab from "./sales/FoliesTab";
import KennisbankTab from "./sales/KennisbankTab";
import ChatbotWidget from "./sales/ChatbotWidget";
import { Sparkles, X } from "lucide-react";

const TIPS = {
  dashboard: "Hier zie je in één oogopslag je openstaande acties, afspraken en verkoopkansen.",
  acties: "Zet hier je taken/to-do's met een status. Openstaande acties komen ook in de dagelijkse herinneringsmail.",
  afspraken: "Plan hier bezoek- of belafspraken met datum, klant en een notitie.",
  offertes: "Maak een offerte: kies de klant, voeg productregels toe en download de PDF. Bij downloaden kun je aanvinken wat je verbergt (bv. het totaalbedrag).",
  orders: "Uit een geaccepteerde offerte maak je met één klik een order.",
  pijplijn: "Sleep verkoopkansen door de fases (van lead tot gewonnen) om je pijplijn bij te houden.",
  aanvragen: "Registreer hier binnengekomen productaanvragen van klanten en volg ze op.",
  klanten: "Voeg klanten toe en klik op een klant om het volledige dossier te bekijken.",
  artikelen: "Beheer hier welke artikelen kiesbaar zijn op offertes en orders.",
  folies: "Beheer hier de verpakkingsfolies die je per offerteregel kunt kiezen.",
  kennisbank: "Voed hier de kennis van Larissa. Alles wat je toevoegt gebruikt de bot direct.",
};
const TIPS_KEY = "larissa_tips_dismissed";
const TOUR_DONE_KEY = "larissa_tour_done";
const TAB_LABELS = {
  dashboard: "Dashboard", acties: "Open acties", afspraken: "Afspraken", offertes: "Offertes",
  orders: "Orders", pijplijn: "Pijplijn", aanvragen: "Productaanvragen", klanten: "Klanten",
  artikelen: "Artikelen", folies: "Folies", kennisbank: "Larissa (kennis)",
};

export default function SalesApp() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [klanten, setKlanten] = useState([]);
  const [afspraken, setAfspraken] = useState([]);
  const [acties, setActies] = useState([]);
  const [offertes, setOffertes] = useState([]);
  const [orders, setOrders] = useState([]);
  const [kansen, setKansen] = useState([]);
  const [artikelen, setArtikelen] = useState([]);
  const [medewerkers, setMedewerkers] = useState([]);
  const [aanvragen, setAanvragen] = useState([]);
  const [artikelenCfg, setArtikelenCfg] = useState({ all: [], blocklist: [], custom: [], folies: [] });
  const canManage = user?.role === "Bedrijfsleiding" || user?.role === "Bedrijfsleiding 2";
  const [tab, setTab] = useState("dashboard");
  const [dismissed, setDismissed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(TIPS_KEY)) || []; } catch (e) { return []; }
  });
  const dismissTip = (t) => {
    setDismissed((d) => { const n = Array.from(new Set([...d, t])); try { localStorage.setItem(TIPS_KEY, JSON.stringify(n)); } catch (e) { /* ignore */ } return n; });
  };

  const [tourStep, setTourStep] = useState(null);
  const tourTabs = ["dashboard", "acties", "afspraken", "offertes", "orders", "pijplijn", "aanvragen", "klanten", ...(canManage ? ["artikelen", "folies", "kennisbank"] : [])];
  const startTour = () => setTourStep(0);
  const endTour = () => { try { localStorage.setItem(TOUR_DONE_KEY, "1"); } catch (e) { /* ignore */ } setTourStep(null); };
  useEffect(() => { if (tourStep != null && tourTabs[tourStep]) setTab(tourTabs[tourStep]); }, [tourStep]); // eslint-disable-line
  useEffect(() => {
    try { if (!localStorage.getItem(TOUR_DONE_KEY)) { const t = setTimeout(() => setTourStep(0), 900); return () => clearTimeout(t); } } catch (e) { /* ignore */ }
  }, []);

  const loadAll = () => {
    api.get("/sales/klanten").then((r) => setKlanten(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/sales/afspraken").then((r) => setAfspraken(r.data)).catch(() => {});
    api.get("/sales/acties").then((r) => setActies(r.data)).catch(() => {});
    api.get("/sales/offertes").then((r) => setOffertes(r.data)).catch(() => {});
    api.get("/sales/orders").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/sales/kansen").then((r) => setKansen(r.data)).catch(() => {});
    api.get("/sales/productaanvragen").then((r) => setAanvragen(r.data)).catch(() => {});
    api.get("/sales/artikelen").then((r) => { setArtikelen(r.data.artikelen || []); setArtikelenCfg({ all: r.data.all || [], blocklist: r.data.blocklist || [], custom: r.data.custom || [], folies: r.data.folies || [] }); }).catch(() => {});
    api.get("/sales/medewerkers").then((r) => setMedewerkers(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  return (
    <div className="min-h-screen bg-transparent">
      <header className="sticky top-0 z-20 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <img src="/clazing-logo.png" alt="Clazing" className="h-9 w-auto object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <div>
            <div className="text-base font-bold text-slate-900 leading-tight">Clazing Sales CRM</div>
            <div className="text-[11px] text-slate-400">v{APP_VERSION} · {APP_BUILD}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <div className="text-sm font-semibold text-slate-800">{user?.name}</div>
            <div className="text-[11px] font-bold text-blue-700">{user?.roleLabel || user?.role}</div>
          </div>
          {(user?.role === "Bedrijfsleiding" || user?.role === "Bedrijfsleiding 2") && (
            <Button variant="outline" size="sm" data-testid="sales-to-prod" onClick={() => navigate("/")}>Productie-app</Button>
          )}
          <Button variant="outline" size="sm" data-testid="sales-tour" onClick={startTour}><Sparkles className="h-4 w-4 mr-1 text-blue-600" /> Rondleiding</Button>
          <Button variant="ghost" size="sm" data-testid="sales-logout" onClick={() => { logout(); navigate("/login"); }}><LogOut className="h-4 w-4 mr-1" /> Uitloggen</Button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl p-4 sm:p-6">
        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList data-testid="sales-tabs" className="flex-wrap h-auto">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard"><LayoutDashboard className="h-4 w-4 mr-1" /> Dashboard</TabsTrigger>
            <TabsTrigger value="acties" data-testid="tab-acties"><ListTodo className="h-4 w-4 mr-1" /> Open acties</TabsTrigger>
            <TabsTrigger value="afspraken" data-testid="tab-afspraken"><CalendarClock className="h-4 w-4 mr-1" /> Afspraken</TabsTrigger>
            <TabsTrigger value="offertes" data-testid="tab-offertes"><FileText className="h-4 w-4 mr-1" /> Offertes</TabsTrigger>
            <TabsTrigger value="orders" data-testid="tab-orders"><Package className="h-4 w-4 mr-1" /> Orders</TabsTrigger>
            <TabsTrigger value="pijplijn" data-testid="tab-pijplijn"><GitBranch className="h-4 w-4 mr-1" /> Pijplijn</TabsTrigger>
            <TabsTrigger value="aanvragen" data-testid="tab-aanvragen"><FlaskConical className="h-4 w-4 mr-1" /> Productaanvragen</TabsTrigger>
            <TabsTrigger value="klanten" data-testid="tab-klanten"><Users2 className="h-4 w-4 mr-1" /> Klanten</TabsTrigger>
            {canManage && <TabsTrigger value="artikelen" data-testid="tab-artikelen"><Boxes className="h-4 w-4 mr-1" /> Artikelen</TabsTrigger>}
            {canManage && <TabsTrigger value="folies" data-testid="tab-folies"><Layers className="h-4 w-4 mr-1" /> Folies</TabsTrigger>}
            {canManage && <TabsTrigger value="kennisbank" data-testid="tab-kennisbank"><BookOpen className="h-4 w-4 mr-1" /> Larissa (kennis)</TabsTrigger>}
          </TabsList>

          {TIPS[tab] && !dismissed.includes(tab) && (
            <div data-testid="larissa-tip" className="mt-4 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
              <span className="mt-0.5 flex h-7 w-7 flex-none items-center justify-center overflow-hidden rounded-full bg-blue-600 ring-2 ring-blue-200"><img src="/larissa-avatar.jpg" alt="Larissa" className="h-full w-full object-cover" /></span>
              <div className="min-w-0 flex-1">
                <p className="text-sm text-slate-700"><b className="text-blue-800">Larissa:</b> {TIPS[tab]}</p>
                <p className="mt-0.5 text-xs text-slate-500">Nieuw hier? Vraag mij gerust hoe dit werkt.</p>
              </div>
              <button data-testid="larissa-tip-ask" onClick={() => window.dispatchEvent(new CustomEvent("open-larissa"))} className="flex-none rounded-md bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700 transition-colors">Vraag Larissa</button>
              <button data-testid="larissa-tip-close" onClick={() => dismissTip(tab)} title="Tip verbergen" className="flex-none rounded-md p-1 text-slate-400 hover:bg-blue-100 hover:text-slate-600 transition-colors"><X className="h-4 w-4" /></button>
            </div>
          )}

          <TabsContent value="dashboard" className="mt-4">
            <DashboardTab klanten={klanten} afspraken={afspraken} acties={acties} offertes={offertes} kansen={kansen} medewerkers={medewerkers} orders={orders} />
          </TabsContent>
          <TabsContent value="acties" className="mt-4">
            <ActiesTab klanten={klanten} artikelen={artikelen} acties={acties} medewerkers={medewerkers} reload={loadAll} />
          </TabsContent>
          <TabsContent value="afspraken" className="mt-4">
            <AfsprakenTab klanten={klanten} artikelen={artikelen} afspraken={afspraken} reload={loadAll} />
          </TabsContent>
          <TabsContent value="offertes" className="mt-4">
            <OffertesTab klanten={klanten} artikelen={artikelen} offertes={offertes} folies={artikelenCfg.folies} medewerkers={medewerkers} user={user} reload={loadAll} />
          </TabsContent>
          <TabsContent value="orders" className="mt-4">
            <OrdersTab orders={orders} reload={loadAll} />
          </TabsContent>
          <TabsContent value="pijplijn" className="mt-4">
            <PijplijnTab klanten={klanten} kansen={kansen} reload={loadAll} />
          </TabsContent>
          <TabsContent value="aanvragen" className="mt-4">
            <AanvragenTab klanten={klanten} aanvragen={aanvragen} reload={loadAll} user={user} />
          </TabsContent>
          <TabsContent value="klanten" className="mt-4">
            <KlantenTab klanten={klanten} afspraken={afspraken} acties={acties} offertes={offertes} kansen={kansen} reload={loadAll} />
          </TabsContent>
          {canManage && (
            <TabsContent value="artikelen" className="mt-4">
              <ArtikelenTab cfg={artikelenCfg} reload={loadAll} />
            </TabsContent>
          )}
          {canManage && (
            <TabsContent value="folies" className="mt-4">
              <FoliesTab folies={artikelenCfg.folies} reload={loadAll} />
            </TabsContent>
          )}
          {canManage && (
            <TabsContent value="kennisbank" className="mt-4">
              <KennisbankTab />
            </TabsContent>
          )}
        </Tabs>
      </main>
      {tourStep != null && (() => {
        const key = tourTabs[tourStep];
        const total = tourTabs.length;
        const first = tourStep === 0;
        const last = tourStep === total - 1;
        return (
          <div data-testid="larissa-tour" className="fixed inset-0 z-40 flex items-end justify-center bg-black/20 p-4 sm:items-center">
            <div className="w-full max-w-md space-y-3 rounded-xl border border-blue-200 bg-white p-5 shadow-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-blue-600 ring-2 ring-blue-200"><img src="/larissa-avatar.jpg" alt="Larissa" className="h-full w-full object-cover" /></span>
                  <div>
                    <div className="text-sm font-bold text-slate-900 leading-tight">Rondleiding met Larissa</div>
                    <div className="text-[11px] text-slate-400">Stap {tourStep + 1} van {total} · {TAB_LABELS[key]}</div>
                  </div>
                </div>
                <button data-testid="tour-close" onClick={endTour} title="Sluiten" className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"><X className="h-4 w-4" /></button>
              </div>
              <p className="text-sm text-slate-700">{first ? "Welkom bij het Sales CRM! Ik leid je even langs de tabbladen. " : ""}<b className="text-blue-800">{TAB_LABELS[key]}:</b> {TIPS[key]}</p>
              <div className="flex items-center justify-center gap-1.5">
                {tourTabs.map((_, i) => <span key={i} className={`h-1.5 rounded-full transition-all ${i === tourStep ? "w-4 bg-blue-600" : "w-1.5 bg-slate-200"}`} />)}
              </div>
              <div className="flex items-center justify-between pt-1">
                <button data-testid="tour-prev" disabled={first} onClick={() => setTourStep((s) => Math.max(0, s - 1))} className="rounded-md px-3 py-1.5 text-xs font-semibold text-slate-500 hover:bg-slate-100 disabled:opacity-40">Vorige</button>
                <button data-testid="tour-next" onClick={() => (last ? endTour() : setTourStep((s) => s + 1))} className="rounded-md bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-blue-700">{last ? "Klaar" : "Volgende"}</button>
              </div>
            </div>
          </div>
        );
      })()}
      <ChatbotWidget />
    </div>
  );
}
